import { inject } from '@angular/core';
import { CanLoadFn, CanMatchFn, Route, Router, UrlTree } from '@angular/router';
import { AppConfig } from '@mkp/shared/util-app-config';
import { Store } from '@ngrx/store';
import { filter, map, Observable, take } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { selectIsBoUser } from '@mkp/user/state-logged-in/selectors';
import { isNotNull } from '@mkp/shared/util-guards';
import { selectWorkspaceFromUrlStatus, WorkspaceFromUrlStatus } from '@mkp/auth/state';
import { selectActiveAccounts } from '@mkp/account/state';

export const inactiveUserGuard: (route: Route) => Observable<true | UrlTree> = ((route) => {
  const store = inject(Store);
  const router = inject(Router);

  return store.select(selectWorkspaceFromUrlStatus).pipe(
    filter((status) => status !== WorkspaceFromUrlStatus.INIT),
    concatLatestFrom(() => [
      store.select(selectIsBoUser).pipe(filter(isNotNull)),
      store.select(selectActiveAccounts).pipe(map((accounts) => accounts?.length > 0)),
    ]),
    take(1),
    map(([, isBoUser, hasAccounts]) => {
      // if user is inactive : go on with routing, otherwise redirect the app
      return (
        isNotBoUserOrHasNoAccountMembership(isBoUser, hasAccounts) ||
        router.parseUrl(getActiveRedirectRoute(route.path ?? '', isBoUser))
      );
    })
  );
}) satisfies CanMatchFn | CanLoadFn; // make sure it can be used as `canMatch` and `canLoad`

const isNotBoUserOrHasNoAccountMembership = (isBoUser: boolean, hasAccountMembership: boolean) => {
  return isBoUser ? false : !hasAccountMembership;
};

// logged-in user trying to reach /unsubscribe are redirected to their profile to manage their marketing-consent
const getActiveRedirectRoute = (route: string, isBoUser: boolean): string => {
  return isBoUser
    ? `/${AppConfig.routes.dashboard}`
    : route.includes(AppConfig.routes.unsubscribe)
      ? `/${AppConfig.routes.account}`
      : `/${AppConfig.routes.dashboard}`;
};
